import { useEffect } from "react";
import { Modal, Form, Input, InputNumber, DatePicker, Select, Button, message } from "antd";
import { DeleteOutlined, SaveOutlined, RollbackOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import ImagePicker from "../../form/ImagePicker";
import { useAddRefundMutation, useAddMembershipRefundMutation, useAddAddonMembershipRefundMutation, useDeleteRefundMutation } from "../../../services/refund";
import "./styles.scss";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface RefundInvoice {
  _id?:             string;
  invoiceNumber?:   string;
  planId?:          { name?: string };
  packageName?:     string;
  totalAmount?:     number;
  startDate?:       string;
  expiryDate?:      string;
  userId?:          { name?: string; phoneNumber?: string };
  customerName?:    string;
  customerNumber?:  string;
}

interface RefundModalProps {
  open:          boolean;
  invoice:       RefundInvoice | null;
  // When set, refunds through the membership (not the invoice) — the Delete
  // action is hidden since there's no confirmed "delete membership refund" endpoint.
  membershipId?: string;
  // Addon memberships refund through a different endpoint/permission than
  // regular memberships; the form/UI is identical either way.
  isAddon?:      boolean;
  onClose:       () => void;
  // Lets the caller refetch its own list (the mutation lives in a different
  // RTK Query API slice, so it can't auto-invalidate that list's cache).
  onSuccess?:    () => void;
}

interface RefundFormValues {
  refundAmount:       number;
  expiryDate?:        dayjs.Dayjs;
  paymentRefundDate?: dayjs.Dayjs;
  remarksType:        'text' | 'image' | 'image and text';
  remarksText?:       string;
  remarksImage?:      string;
}

const remarksTypeOptions = [
  { value: 'text',            label: 'Text'          },
  { value: 'image',           label: 'Image'         },
  { value: 'image and text',  label: 'Image and Text' },
];

/* ─── Component ──────────────────────────────────────────────────────── */

const RefundModal = ({ open, invoice, membershipId, isAddon, onClose, onSuccess }: RefundModalProps) => {
  const [form] = Form.useForm<RefundFormValues>();
  const [addRefund,              { isLoading: savingInvoice }]      = useAddRefundMutation();
  const [addMembershipRefund,    { isLoading: savingMembership }]   = useAddMembershipRefundMutation();
  const [addAddonMembershipRefund, { isLoading: savingAddon }]      = useAddAddonMembershipRefundMutation();
  const [deleteRefund,           { isLoading: deleting }]           = useDeleteRefundMutation();
  const saving = savingInvoice || savingMembership || savingAddon;

  const packageName    = invoice?.planId?.name || invoice?.packageName || '-';
  const invoiceAmount  = invoice?.totalAmount ?? 0;
  const customerName   = invoice?.userId?.name || invoice?.customerName || '-';
  const customerMobile = invoice?.userId?.phoneNumber || invoice?.customerNumber || '-';
  const remarksType    = Form.useWatch('remarksType', form);
  const minExpiry      = invoice?.startDate  ? dayjs(invoice.startDate)  : null;
  const maxExpiry      = invoice?.expiryDate ? dayjs(invoice.expiryDate) : null;

  // Expiry Date can only be picked between the invoice's own start and expiry dates.
  const disabledExpiryDate = (current: dayjs.Dayjs) => {
    if (!current) return false;
    if (minExpiry && current.isBefore(minExpiry, 'day')) return true;
    if (maxExpiry && current.isAfter(maxExpiry, 'day')) return true;
    return false;
  };

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      expiryDate: invoice?.expiryDate ? dayjs(invoice.expiryDate) : undefined,
    });
  }, [open, invoice, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSave = async (values: RefundFormValues) => {
    if (!membershipId && !invoice?._id) return;
    const body = {
      refundAmount:      values.refundAmount,
      expiryDate:        values.expiryDate ? dayjs(values.expiryDate).format('YYYY-MM-DD') : undefined,
      paymentRefundDate: values.paymentRefundDate ? dayjs(values.paymentRefundDate).format('YYYY-MM-DD') : undefined,
      remarksType:       values.remarksType,
      remarksText:       values.remarksType !== 'image' ? values.remarksText  : undefined,
      remarksImage:      values.remarksType !== 'text'  ? values.remarksImage : undefined,
    };
    try {
      if (membershipId && isAddon) {
        await addAddonMembershipRefund({ membershipId, ...body }).unwrap();
      } else if (membershipId) {
        await addMembershipRefund({ membershipId, ...body }).unwrap();
      } else {
        await addRefund({ invoiceId: invoice!._id as string, ...body }).unwrap();
      }
      // message.success('Refund saved successfully');
      onSuccess?.();
      handleClose();
    } catch {
      /* error toast handled globally by badRequestHandler */
    }
  };

  const handleDelete = () => {
    if (!invoice?._id) return;
    Modal.confirm({
      title:      'Delete Refund',
      content:    `Are you sure you want to delete the refund for invoice "${invoice.invoiceNumber}"?`,
      okText:     'Delete',
      okType:     'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteRefund(invoice._id as string).unwrap();
          message.success('Refund deleted successfully');
          handleClose();
        } catch {
          /* error toast handled globally by badRequestHandler */
        }
      },
    });
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={
        <span className="refund-modal-title">
          <span className="refund-modal-icon"><RollbackOutlined /></span>
          Refund
        </span>
      }
      footer={null}
      destroyOnClose
      width={640}
      centered
      className="refund-modal"
    >
      <div className="refund-modal-customer">
        <div className="refund-modal-customer-item">
          <span className="label">Name</span>
          <span className="value">{customerName}</span>
        </div>
        <div className="refund-modal-customer-item">
          <span className="label">Mobile</span>
          <span className="value">{customerMobile}</span>
        </div>
      </div>

      <Form form={form} layout="vertical" onFinish={handleSave} className="refund-form">
        <div className="row">
          <Form.Item label="Package Name">
            <Input value={packageName} disabled />
          </Form.Item>

          <Form.Item label="Invoice Amount">
            <InputNumber
              style={{ width: '100%' }}
              value={invoiceAmount}
              disabled
              formatter={(v) => `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            />
          </Form.Item>
        </div>

        <div className="row">
          <Form.Item
            name="refundAmount"
            label="Refund Amount"
            rules={[
              { required: true, message: 'Please enter refund amount' },
              {
                validator: (_, value) => {
                  if (value != null && invoiceAmount && value > invoiceAmount) {
                    return Promise.reject(new Error(`Cannot exceed invoice amount (₹${invoiceAmount.toLocaleString()})`));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              placeholder="Enter refund amount"
            />
          </Form.Item>

          <Form.Item name="expiryDate" label="Expiry Date" rules={[{ required: true, message: 'Please select expiry date' }]}>
            <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" disabledDate={disabledExpiryDate} />
          </Form.Item>
        </div>

        <div className="row">
          <Form.Item name="paymentRefundDate" label="Payment Refund Date" rules={[{ required: true, message: 'Please select payment refund date' }]}>
            <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
          </Form.Item>

          <Form.Item name="remarksType" label="Remarks Type" rules={[{ required: true, message: 'Please select remarks type' }]}>
            <Select
              placeholder="Select Remarks Type"
              options={remarksTypeOptions}
              onChange={() => form.setFieldsValue({ remarksText: undefined, remarksImage: undefined })}
            />
          </Form.Item>
        </div>

        {(remarksType === 'text' || remarksType === 'image and text') && (
          <Form.Item name="remarksText" label="Remarks" className="field-span-2" rules={[{ required: true, message: 'Please enter remarks' }]}>
            <Input.TextArea placeholder="Enter remarks" rows={3} />
          </Form.Item>
        )}

        {(remarksType === 'image' || remarksType === 'image and text') && (
          <Form.Item name="remarksImage" label="Remarks Image" className="field-span-2" rules={[{ required: true, message: 'Please upload an image' }]}>
            <ImagePicker form={form} name="remarksImage" />
          </Form.Item>
        )}

        <div className="refund-modal-footer">
          {!membershipId && (
            <Button danger icon={<DeleteOutlined />} loading={deleting} onClick={handleDelete}>
              Delete
            </Button>
          )}
          <Button type="primary" icon={<SaveOutlined />} htmlType="submit" loading={saving}>
            Save
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default RefundModal;
